// src/features/gift-cards/pages/GiftCardPage.tsx
// ============================================================================
// PÁGINA OPERATIVA DE GIFT CARDS — EXCLUSIVA PREMIUM
// ============================================================================
//
// v2.0.0 - Campo "De parte de:" (senderName)
//        - Variable {remitente} en mensajes
//        - Selector de diseño vive aquí (no en config)
//        - senderName en Canvas, webhook y Firestore
//
// Permite a usuarios Premium crear y enviar gift cards:
//   1. Buscar cliente existente (ES) o ingresar número nuevo
//   2. Indicar de parte de quién (senderName)
//   3. Seleccionar puntos (presets o personalizado)
//   4. Elegir diseño de tarjeta (4 templates Canvas)
//   5. Previsualizar la tarjeta generada
//   6. Confirmar envío → Firestore + webhook n8n → WhatsApp
//
// Ubicación: src/features/gift-cards/pages/GiftCardPage.tsx
// Ruta: /dashboard/gift-cards
// Acceso: plan PRO o superior (isPro) — Premium fusionado en PRO (2026-06-25)
//
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import Layout from '../../../components/layout/Layout';
import PestanasModulo from '../../../components/common/PestanasModulo';
import { GIFT_TABS } from '../pestanas';
import { isPro as checkIsPremium } from '../../../utils/planPermissions';
import { ProLocked } from '../../../components/pro/ProLocked';
import SearchInput, { type SearchResultItem } from '../../../components/common/SearchInput';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import BrandLoader from '../../../components/common/BrandLoader';
import { giftcardsApi, type GiftCardMode } from '../../../api/giftcardsApi';
import {
  CreditCard,
  Loader2,
  CheckCircle,
  AlertCircle,
  Info,
  Send,
  UserPlus,
  Gift,
  Sparkles,
  Eye,
  X,
  Clock,
  User,
  Phone,
  ChevronDown,
  Heart,
  MessageSquareHeart,
} from 'lucide-react';
import {
  GIFT_CARD_TEMPLATES,
  generateGiftCardPreview,
  generateGiftCardImage,
  toSendableImage,
  type GiftCardTemplateId,
  type AnyGiftCardTemplateId,
  type GiftCardTemplateInfo,
  type GiftCardCustomDesign,
} from '../utils/giftCardTemplates';

// ============================================================================
// CONSTANTES
// ============================================================================

const POINT_PRESETS = [10, 20, 50, 100, 500, 1000];

// Catálogo compartido (src/lib/countries.ts) proyectado al shape local {code:'+52',…}.
// Nota: aquí siempre se ofrecen todos (así era antes con 6 países, sin flag) — el
// destinatario de una tarjeta se guarda con lada completa desde siempre.
import { PHONE_COUNTRIES } from '../../../lib/countries';
const COUNTRIES = PHONE_COUNTRIES.map((c) => ({ code: c.prefix, name: c.name, flag: c.flag, maxDigits: c.nationalDigits }));

// ============================================================================
// TIPOS
// ============================================================================

interface SelectedClient {
  id: string;
  name: string | null;
  phone: string;
  countryCode: string;
  points: number;
}

interface GiftCardHistoryItem {
  id: string;
  recipientName: string | null;
  recipientPhone: string;
  senderName: string | null;
  message: string | null;
  points: number;
  /** 'money' = `points` son pesos de saldo de regalo (14-oct-2026). */
  unit: GiftCardMode;
  status: 'pending' | 'redeemed' | 'cancelled';
  templateId: GiftCardTemplateId;
  createdAt: Date;
}

// ============================================================================
// HELPERS
// ============================================================================

const formatPoints = (points: number, loyaltyMode: string): string => {
  if (loyaltyMode === 'cashback') return `$${points.toFixed(2)}`;
  return `${points} pts`;
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const GiftCardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { clearErrors } = useError();
  const navigate = useNavigate();

  // ── Permisos ──
  const hasPremiumAccess = checkIsPremium(currentUser?.subscription);
  const businessId = currentUser?.businessId;
  const loyaltyModeReal = currentUser?.loyaltyMode || 'visits';
  // Modo de la gift card (14-oct-2026, Marrieto #1400): en 'money' se regalan PESOS al saldo de
  // regalo (se cobra en la caja), no puntos. Toda la pantalla habla entonces en «$», igual que un
  // negocio de cashback; los puntos del cliente (su saldo de lealtad) se siguen mostrando como son.
  const [giftMode, setGiftMode] = useState<GiftCardMode>('points');
  const moneyMode = giftMode === 'money';
  const loyaltyMode = moneyMode ? 'cashback' : loyaltyModeReal;

  // ── Estado: cliente ──
  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(null);
  const [isNewRecipient, setIsNewRecipient] = useState(false);
  const [manualPhone, setManualPhone] = useState('');
  const [manualCountryCode, setManualCountryCode] = useState('+52');
  const [manualName, setManualName] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // ── Estado: remitente ──
  const [senderName, setSenderName] = useState('');
  // Mensaje personal (14-oct-2026, Marrieto #1396): se agrega al final del texto del WhatsApp.
  const [personalMessage, setPersonalMessage] = useState('');
  const MENSAJE_MAX = 300;

  // ── Estado: puntos ──
  const [selectedPoints, setSelectedPoints] = useState<number | null>(null);
  const [customPoints, setCustomPoints] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  // ── Estado: template ──
  const [selectedTemplateId, setSelectedTemplateId] = useState<AnyGiftCardTemplateId>('general');
  // Diseño propio del tenant (settings.custom_design); null = no tiene
  const [customDesign, setCustomDesign] = useState<GiftCardCustomDesign | null>(null);

  // ── Estado: preview ──
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // ── Estado: envío ──
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Resultado real del backend (acreditó al instante o quedó pendiente; saldo; aviso shadow).
  const [sentResult, setSentResult] = useState<{ credited: boolean; balance: number | null; shadow?: boolean } | null>(null);

  // ── Estado: historial ──
  const [history, setHistory] = useState<GiftCardHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Estado: config del negocio ──
  const [giftCardConfig, setGiftCardConfig] = useState<{
    enabled: boolean;
    template: string;
    googleMapsUrl: string;
  } | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  // ============================================================================
  // CARGAR CONFIG + HISTORIAL
  // ============================================================================

  useEffect(() => {
    if (!hasPremiumAccess || !businessId) {
      setConfigLoading(false);
      return;
    }

    const loadData = async () => {
      setConfigLoading(true);

      // Cargar config (lealify-base: /giftcards/settings)
      try {
        const { settings } = await giftcardsApi.getSettings();
        setGiftMode(settings.mode === 'money' ? 'money' : 'points');
        setGiftCardConfig({
          enabled: settings.enabled === true,
          template: settings.template || '',
          googleMapsUrl: settings.google_maps_url || '',
        });
        // Diseño propio del negocio: si existe, se ofrece y queda preseleccionado
        if (settings.custom_design?.enabled && settings.custom_design.background_url) {
          setCustomDesign(settings.custom_design);
          setSelectedTemplateId('custom');
        } else {
          setCustomDesign(null);
        }
      } catch (err) {
        console.error('Error cargando config gift cards:', err);
      }

      setConfigLoading(false);

      // Pre-llenar remitente con nombre del negocio
      if (currentUser?.businessName) {
        setSenderName(currentUser.businessName);
      }

      // Cargar historial
      fetchHistory();
    };

    loadData();
  }, [hasPremiumAccess, businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================
  // HISTORIAL DE GIFT CARDS
  // ============================================================================

  const fetchHistory = useCallback(async () => {
    if (!businessId) return;
    setHistoryLoading(true);

    try {
      // lealify-base: /giftcards (últimas 200, orden desc). Tomamos las 20 más recientes.
      const { gift_cards } = await giftcardsApi.list();
      const items: GiftCardHistoryItem[] = (gift_cards || []).slice(0, 20).map((gc) => ({
        id: String(gc.id),
        recipientName: gc.recipient_name || null,
        recipientPhone: gc.recipient_phone || '',
        senderName: gc.sender_name || null,
        message: gc.message || null,
        points: gc.points ?? 0,
        unit: gc.unit === 'money' ? 'money' : 'points',
        status: gc.status || 'redeemed',
        // base no persiste el diseño en el listado → se muestra el genérico.
        templateId: 'general',
        createdAt: gc.created_at ? new Date(gc.created_at) : new Date(),
      }));
      setHistory(items);
    } catch (err) {
      console.error('Error cargando historial gift cards:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [businessId]);

  // ============================================================================
  // SELECCIÓN DE CLIENTE DESDE SEARCH
  // ============================================================================

  const handleClientSelect = useCallback((client: SearchResultItem) => {
    setSelectedClient({
      id: client.id,
      name: client.name,
      phone: client.phone,
      countryCode: client.countryCode || '+52',
      points: client.points || 0,
    });
    setIsNewRecipient(false);
    setManualPhone('');
    setManualName('');
    setSendSuccess(false);
    setSendError(null);
  }, []);

  const handleSearchClear = useCallback((term: string | null) => {
    if (!term) {
      setSelectedClient(null);
      setIsNewRecipient(false);
    }
  }, []);

  // ============================================================================
  // MODO "NUEVO DESTINATARIO"
  // ============================================================================

  const handleToggleNewRecipient = useCallback(() => {
    setSelectedClient(null);
    setIsNewRecipient(true);
    setManualPhone('');
    setManualName('');
    setSendSuccess(false);
    setSendError(null);
  }, []);

  const handleCancelNewRecipient = useCallback(() => {
    setIsNewRecipient(false);
    setManualPhone('');
    setManualName('');
  }, []);

  // ============================================================================
  // PUNTOS
  // ============================================================================

  const effectivePoints = useMemo(() => {
    if (isCustom) {
      const val = parseFloat(customPoints);
      return isNaN(val) || val <= 0 ? null : val;
    }
    return selectedPoints;
  }, [isCustom, customPoints, selectedPoints]);

  // ============================================================================
  // DISEÑOS DISPONIBLES (los 6 integrados + el propio del negocio si existe)
  // ============================================================================

  const availableTemplates = useMemo<GiftCardTemplateInfo[]>(() => {
    if (!customDesign) return GIFT_CARD_TEMPLATES;
    return [
      {
        id: 'custom',
        label: `⭐ ${customDesign.label || 'Diseño de tu negocio'}`,
        description: 'El diseño propio de tu negocio, con los datos escritos encima',
        icon: '⭐',
        previewColors: ['#1c1917', customDesign.font?.color || '#e79cc2'],
      },
      ...GIFT_CARD_TEMPLATES,
    ];
  }, [customDesign]);

  // ============================================================================
  // GENERAR PREVIEW
  // ============================================================================

  const handleGeneratePreview = useCallback(async () => {
    if (!effectivePoints) return;

    setGeneratingPreview(true);
    try {
      const recipientName = selectedClient?.name || manualName || 'Cliente';
      const preview = await generateGiftCardPreview({
        templateId: selectedTemplateId,
        businessName: currentUser?.businessName || 'Mi Negocio',
        recipientName,
        points: effectivePoints,
        primaryColor: currentUser?.businessColors?.primary || '#0891b2',
        logoUrl: currentUser?.logoUrl || null,
        loyaltyMode,
        giftMoney: moneyMode,
        senderName: senderName || undefined,
        customDesign: customDesign || undefined,
      });
      setPreviewUrl(preview);
      setShowPreview(true);
    } catch (err) {
      console.error('Error generando preview:', err);
    } finally {
      setGeneratingPreview(false);
    }
  }, [effectivePoints, selectedClient, manualName, selectedTemplateId, currentUser, loyaltyMode, moneyMode, senderName, customDesign]);

  // Preview automático: en cuanto hay destinatario y puntos, se dibuja la tarjeta tal
  // cual va a salir (y se rehace al cambiar nombre, puntos, remitente o diseño), para
  // no enviar a ciegas. Debounce para no redibujar en cada tecla. Irvin/Marrieto 9-oct.
  const previewReady = !!effectivePoints && !!(selectedClient || (isNewRecipient && manualPhone.length >= 9));
  useEffect(() => {
    if (!previewReady || sending) return;
    const t = setTimeout(() => { handleGeneratePreview(); }, 500);
    return () => clearTimeout(t);
  }, [previewReady, handleGeneratePreview, sending]);

  // ============================================================================
  // VALIDACIÓN
  // ============================================================================

  const canSend = useMemo(() => {
    if (!effectivePoints || effectivePoints <= 0) return false;
    if (!senderName.trim()) return false;
    if (selectedClient) return true;
    if (isNewRecipient && manualPhone.replace(/\D/g, '').length >= 9) return true;
    return false;
  }, [effectivePoints, senderName, selectedClient, isNewRecipient, manualPhone]);

  // ============================================================================
  // ENVIAR GIFT CARD
  // ============================================================================

  const handleSend = useCallback(async () => {
    if (!businessId || !effectivePoints || !canSend) return;
    if (clearErrors) clearErrors();
    setSendError(null);
    setSentResult(null);
    setSending(true);

    try {
      // Teléfono: cliente seleccionado → tal cual lo guarda base (10 díg. locales, así
      // base lo empata y acredita); destinatario nuevo → con lada internacional.
      const recipientPhone = selectedClient
        ? selectedClient.phone.replace(/\D/g, '')
        : `${manualCountryCode}${manualPhone}`.replace(/\D/g, '');
      const recipientName = selectedClient?.name || manualName || undefined;
      const trimmedSenderName = senderName.trim();

      // ── Imagen de la tarjeta (Canvas → data-URI); base la manda por sendImage ──
      let image: string | undefined;
      try {
        image = await toSendableImage(await generateGiftCardImage({
          templateId: selectedTemplateId,
          businessName: currentUser?.businessName || 'Mi Negocio',
          recipientName: recipientName || 'Cliente',
          points: effectivePoints,
          primaryColor: currentUser?.businessColors?.primary || '#0891b2',
          logoUrl: currentUser?.logoUrl || null,
          loyaltyMode,
          giftMoney: moneyMode,
          senderName: trimmedSenderName,
          customDesign: customDesign || undefined,
        }));
      } catch (imgErr) {
        console.error('Error generando imagen de la tarjeta:', imgErr);
      }

      // ── Crear tarjeta en lealify-base (acredita si ya es cliente, o deja pendiente;
      //     el aviso WhatsApp lo manda base con su plantilla @nombre/@remitente/@puntos/
      //     @negocio/@direccion — shadow si el negocio no tiene WhatsApp). ──
      const result = await giftcardsApi.create({
        recipient_phone: recipientPhone,
        points: effectivePoints,
        recipient_name: recipientName,
        sender_name: trimmedSenderName || undefined,
        message: personalMessage.trim() || undefined,
        template_id: selectedTemplateId,
        image,
      });

      // ── Reflejar saldo nuevo si se acreditó a un cliente seleccionado ──
      // (En pesos el saldo devuelto es el de REGALO, no sus puntos: no se toca.)
      if (result.credited && result.balance != null && selectedClient && result.unit !== 'money') {
        setSelectedClient({ ...selectedClient, points: result.balance });
      }

      setSentResult({ credited: result.credited, balance: result.balance, shadow: result.whatsapp?.shadow });
      setSendSuccess(true);
      setSelectedPoints(null);
      setCustomPoints('');
      setIsCustom(false);
      setPersonalMessage('');
      setPreviewUrl(null);
      setShowPreview(false);

      // Recargar historial
      fetchHistory();

      setTimeout(() => setSendSuccess(false), 6000);
    } catch (err) {
      console.error('Error enviando gift card:', err);
      setSendError('Error al enviar la gift card. Intenta de nuevo.');
    } finally {
      setSending(false);
    }
  }, [
    businessId, effectivePoints, canSend, selectedClient, manualPhone,
    manualCountryCode, manualName, senderName, personalMessage, selectedTemplateId,
    currentUser, loyaltyMode, fetchHistory, clearErrors, customDesign,
  ]);

  // ============================================================================
  // RESETEAR FORMULARIO
  // ============================================================================

  const handleReset = useCallback(() => {
    setSelectedClient(null);
    setIsNewRecipient(false);
    setManualPhone('');
    setManualName('');
    setSenderName(currentUser?.businessName || '');
    setPersonalMessage('');
    setSelectedPoints(null);
    setCustomPoints('');
    setIsCustom(false);
    setPreviewUrl(null);
    setShowPreview(false);
    setSendSuccess(false);
    setSendError(null);
    setSentResult(null);
  }, [currentUser?.businessName]);

  // ============================================================================
  // GATE: NO PREMIUM
  // ============================================================================

  if (!hasPremiumAccess) {
    return (
      <Layout>
        <div className="space-y-6 px-2 sm:px-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-teal-500" />
              Gift Cards
            </h1>
          </div>
          <ProLocked feature="gift-cards" />
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // LOADING
  // ============================================================================

  if (configLoading) {
    return (
      <Layout>
        <div className="space-y-6 px-2 sm:px-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-teal-500" />
              Gift Cards
            </h1>
          </div>
          <div className="bg-surface shadow rounded-lg p-8 text-center">
            <BrandLoader size={36} />
            <p className="text-content-3 text-sm">Cargando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // WARNING: CONFIG NO HABILITADA
  // ============================================================================

  const configNotEnabled = !giftCardConfig?.enabled;

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  const maxDigits = COUNTRIES.find((c) => c.code === manualCountryCode)?.maxDigits || 10;
  const hasRecipient = selectedClient || (isNewRecipient && manualPhone.length >= 9);

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        {/* Header */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-teal-500" />
            Gift Cards
          </h1>
          <p className="mt-1 text-sm text-content-2">
            {moneyMode
              ? 'Crea y envía gift cards en pesos (saldo de regalo para pagar en tu caja) por WhatsApp'
              : 'Crea y envía gift cards con puntos de regalo por WhatsApp'}
          </p>
        </div>
        <PestanasModulo tabs={GIFT_TABS} activa="enviar" testid="giftcards-tabs" />

        {/* Warning: config no habilitada */}
        {configNotEnabled && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-md animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex">
              <Info className="h-5 w-5 text-amber-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-amber-700">
                  Las Gift Cards están <strong>desactivadas</strong>. Configúralas desde{' '}
                  <button onClick={() => navigate('/dashboard/gift-cards?tab=ajustes')} className="underline font-medium hover:text-amber-900">
                    la pestaña Ajustes
                  </button>{' '}
                  para habilitar el envío por WhatsApp.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Éxito */}
        {sendSuccess && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 rounded-md animate-fade-in-up">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300 font-medium">¡Gift card enviada exitosamente!</p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  {sentResult?.credited
                    ? `${moneyMode ? 'El saldo de regalo se sumó' : 'Los puntos se sumaron'} al cliente${sentResult.balance != null ? ` (saldo${moneyMode ? ' de regalo' : ''}: ${formatPoints(sentResult.balance, loyaltyMode)})` : ''}.`
                    : `${moneyMode ? 'El saldo de regalo quedará pendiente' : 'Los puntos quedarán pendientes'} hasta que el destinatario se registre en tu kiosco.`}
                  {sentResult?.shadow && ' El negocio aún no tiene WhatsApp conectado, así que el mensaje no se envió.'}
                </p>
                <button
                  onClick={handleReset}
                  className="mt-2 text-xs text-green-700 dark:text-green-300 underline hover:text-green-900"
                >
                  Crear otra gift card
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {sendError && (
          <div className="bg-red-50 dark:bg-red-500/10 border-l-4 border-red-400 p-4 rounded-md animate-fade-in-up">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-300">{sendError}</p>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* PASO 1: DESTINATARIO + REMITENTE                                   */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-surface shadow rounded-lg animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 sm:px-6 py-4 border-b border-line">
            <h3 className="text-sm font-semibold text-content flex items-center">
              <User className="h-4 w-4 text-teal-500 mr-2" />
              1. Destinatario y remitente
            </h3>
          </div>
          <div className="p-4 sm:p-6 space-y-5">

            {/* ── ¿A quién va? Selector explícito de modo (cliente existente vs nuevo) ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleCancelNewRecipient}
                disabled={sending}
                className={`rounded-lg border-2 p-3 text-left transition-all ${
                  !isNewRecipient
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10 ring-1 ring-teal-500'
                    : 'border-line bg-surface hover:border-teal-300'
                }`}
              >
                <span className={`flex items-center text-sm font-semibold ${!isNewRecipient ? 'text-teal-700 dark:text-teal-300' : 'text-content-2'}`}>
                  <User className="h-4 w-4 mr-1.5" />
                  Cliente existente
                  {!isNewRecipient && <CheckCircle className="h-4 w-4 ml-auto text-teal-500" />}
                </span>
                <span className="block text-xs text-content-3 mt-1">
                  Ya está registrado: búscalo por nombre o teléfono. {moneyMode ? 'El saldo de regalo se abona' : 'Los puntos se abonan'} al instante.
                </span>
              </button>
              <button
                type="button"
                onClick={handleToggleNewRecipient}
                disabled={sending}
                className={`rounded-lg border-2 p-3 text-left transition-all ${
                  isNewRecipient
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10 ring-1 ring-teal-500'
                    : 'border-line bg-surface hover:border-teal-300'
                }`}
              >
                <span className={`flex items-center text-sm font-semibold ${isNewRecipient ? 'text-teal-700 dark:text-teal-300' : 'text-content-2'}`}>
                  <UserPlus className="h-4 w-4 mr-1.5" />
                  Nueva persona
                  {isNewRecipient && <CheckCircle className="h-4 w-4 ml-auto text-teal-500" />}
                </span>
                <span className="block text-xs text-content-3 mt-1">
                  Aún no es cliente: captura su teléfono. {moneyMode ? 'El saldo queda pendiente' : 'Los puntos quedan pendientes'} hasta que se registre.
                </span>
              </button>
            </div>

            {/* ── Destinatario ── */}
            {!isNewRecipient ? (
              <>
                <div className="relative" style={{ zIndex: 30 }}>
                  <label className="block text-sm font-medium text-content-2 mb-1">
                    Buscar cliente existente
                  </label>
                  <SearchInput
                    onSearch={handleSearchClear}
                    onClientSelect={handleClientSelect}
                    placeholder="Buscar por nombre o teléfono"
                    preventClientNavigation={true}
                    disabled={sending}
                  />
                </div>

                {selectedClient && (
                  <div className="bg-teal-50 dark:bg-teal-500/10 border border-teal-200 rounded-lg p-4 flex items-center justify-between animate-fade-in">
                    <ClientContactInfo
                      clientId={selectedClient.id}
                      clientName={selectedClient.name}
                      phoneNumber={selectedClient.phone}
                      countryCode={selectedClient.countryCode}
                    />
                    <div className="text-right">
                      <p className="text-xs text-content-3">Saldo actual</p>
                      <p className="text-lg font-bold text-teal-700 dark:text-teal-300">
                        {formatPoints(selectedClient.points, loyaltyModeReal)}
                      </p>
                    </div>
                  </div>
                )}

                {!selectedClient && (
                  <p className="text-xs text-content-3">
                    ¿No aparece? Entonces aún no es cliente — usa la opción "Nueva persona" de arriba.
                  </p>
                )}
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-content-2 mb-1">
                    <Phone className="h-4 w-4 inline mr-1 text-teal-500" />
                    Número de teléfono del destinatario
                  </label>
                  <div className="flex">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className="h-full rounded-l-lg border border-r-0 border-line px-3 py-2.5 bg-surface-2 flex items-center space-x-1 text-sm hover:bg-canvas"
                      >
                        <span>{COUNTRIES.find((c) => c.code === manualCountryCode)?.flag}</span>
                        <span className="text-content-2">{manualCountryCode}</span>
                        <ChevronDown className="h-3 w-3 text-content-3" />
                      </button>
                      {showCountryDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-surface border border-line rounded-lg shadow-lg z-50 w-52">
                          {COUNTRIES.map((c) => (
                            <button
                              key={c.code}
                              onClick={() => {
                                setManualCountryCode(c.code);
                                setShowCountryDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-surface-2 flex items-center space-x-2"
                            >
                              <span>{c.flag}</span>
                              <span className="text-content-2">{c.name}</span>
                              <span className="text-content-3 ml-auto">{c.code}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={manualPhone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= maxDigits) setManualPhone(val);
                      }}
                      placeholder={`${maxDigits} dígitos`}
                      className="flex-1 border border-line rounded-r-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      disabled={sending}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-2 mb-1">
                    Nombre del destinatario <span className="text-content-3 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    placeholder="María López"
                    className="w-full border border-line rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    disabled={sending}
                  />
                  <p className="text-xs text-content-3 mt-1">
                    Si no es cliente, {moneyMode ? 'el saldo de regalo se acreditará' : 'los puntos se acreditarán'} al registrarse en el kiosco
                  </p>
                </div>

              </>
            )}

            {/* ── De parte de (remitente) ── */}
            {hasRecipient && (
              <div className="pt-3 border-t border-line animate-fade-in">
                <label className="block text-sm font-medium text-content-2 mb-1">
                  <Heart className="h-4 w-4 inline mr-1.5 text-pink-500" />
                  De parte de
                </label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Tu nombre o el del negocio"
                  className="w-full border border-line rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  disabled={sending}
                />
                <p className="text-xs text-content-3 mt-1">
                  Aparecerá en la tarjeta y en el mensaje de WhatsApp
                </p>

                <label className="block text-sm font-medium text-content-2 mb-1 mt-3">
                  <MessageSquareHeart className="h-4 w-4 inline mr-1.5 text-pink-500" />
                  Mensaje personal <span className="font-normal text-content-3">(opcional)</span>
                </label>
                <textarea
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value.slice(0, MENSAJE_MAX))}
                  placeholder="Ej.: ¡Feliz cumpleaños! Disfruta tu cafecito 💛"
                  rows={3}
                  maxLength={MENSAJE_MAX}
                  className="w-full border border-line rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-y"
                  disabled={sending}
                />
                <p className="text-xs text-content-3 mt-1 flex justify-between gap-2">
                  <span>Se agrega al final del mensaje de WhatsApp que acompaña la tarjeta</span>
                  <span className="tabular-nums shrink-0">{personalMessage.length}/{MENSAJE_MAX}</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* PASO 2: SELECCIONAR PUNTOS                                         */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {hasRecipient && (
          <div className="bg-surface shadow rounded-lg animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="px-4 sm:px-6 py-4 border-b border-line">
              <h3 className="text-sm font-semibold text-content flex items-center">
                <Sparkles className="h-4 w-4 text-teal-500 mr-2" />
                2. Cantidad de {moneyMode ? 'pesos (saldo de regalo)' : loyaltyMode === 'cashback' ? 'saldo' : 'puntos'}
              </h3>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
                {POINT_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => {
                      setSelectedPoints(preset);
                      setIsCustom(false);
                      setCustomPoints('');
                    }}
                    disabled={sending}
                    className={`py-3 rounded-lg text-sm font-semibold border-2 transition-all ${
                      !isCustom && selectedPoints === preset
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 ring-1 ring-teal-500'
                        : 'border-line text-content-2 hover:border-line bg-surface'
                    }`}
                  >
                    {loyaltyMode === 'cashback' ? `$${preset}` : preset}
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    setIsCustom(true);
                    setSelectedPoints(null);
                  }}
                  disabled={sending}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                    isCustom
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300'
                      : 'border-line text-content-2 hover:border-line'
                  }`}
                >
                  Personalizado
                </button>
                {isCustom && (
                  <div className="relative flex-1 max-w-xs animate-fade-in">
                    {loyaltyMode === 'cashback' && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-content-3 text-sm">$</span>
                    )}
                    <input
                      type="number"
                      value={customPoints}
                      onChange={(e) => setCustomPoints(e.target.value)}
                      placeholder={loyaltyMode === 'cashback' ? '0.00' : '0'}
                      min="1"
                      step={loyaltyMode === 'cashback' ? '0.01' : '1'}
                      className={`w-full border border-line rounded-lg py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        loyaltyMode === 'cashback' ? 'pl-7 pr-3' : 'px-3'
                      }`}
                      disabled={sending}
                      autoFocus
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* PASO 3: SELECCIONAR DISEÑO                                         */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {effectivePoints && effectivePoints > 0 && hasRecipient && (
          <div className="bg-surface shadow rounded-lg animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="px-4 sm:px-6 py-4 border-b border-line flex items-center justify-between">
              <h3 className="text-sm font-semibold text-content flex items-center">
                <Gift className="h-4 w-4 text-teal-500 mr-2" />
                3. Diseño de tarjeta
              </h3>
              <button
                onClick={handleGeneratePreview}
                disabled={generatingPreview}
                className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:text-teal-300 font-medium flex items-center"
              >
                {generatingPreview ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Eye className="h-3 w-3 mr-1" />
                )}
                Ver preview
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {availableTemplates.map((tpl) => {
                  const isSelected = selectedTemplateId === tpl.id;
                  return (
                    <button
                      key={tpl.id}
                      onClick={() => {
                        setSelectedTemplateId(tpl.id);
                        setPreviewUrl(null);
                        setShowPreview(false);
                      }}
                      disabled={sending}
                      className={`relative rounded-lg border-2 p-3 text-center transition-all ${
                        isSelected
                          ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10 ring-1 ring-teal-500'
                          : 'border-line hover:border-line bg-surface'
                      }`}
                    >
                      {tpl.id === 'custom' && customDesign ? (
                        // el diseño propio se reconoce por su propia imagen, no por un degradado
                        <div
                          className="h-12 rounded-md mb-2 bg-cover bg-center"
                          style={{ backgroundImage: `url(${customDesign.background_url})` }}
                        />
                      ) : (
                        <div
                          className="h-12 rounded-md mb-2 flex items-center justify-center text-xl"
                          style={{
                            background: `linear-gradient(135deg, ${tpl.previewColors[0]}, ${tpl.previewColors[1]})`,
                          }}
                        >
                          {tpl.icon}
                        </div>
                      )}
                      <span className={`text-xs font-medium ${isSelected ? 'text-teal-700 dark:text-teal-300' : 'text-content-2'}`}>
                        {tpl.label}
                      </span>
                      {isSelected && (
                        <div className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-teal-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {showPreview && previewUrl && (
                <div className="mt-4 flex justify-center animate-fade-in">
                  <div className="relative w-full max-w-md">
                    <p className="text-xs text-content-3 mb-2 text-center">Así le llegará por WhatsApp:</p>
                    {/* h-auto: el diseño propio no es cuadrado (p. ej. 1650×1050) y w-56 h-56 lo aplastaba */}
                    <img
                      src={previewUrl}
                      alt="Preview gift card"
                      className="w-full h-auto rounded-lg shadow-md border border-line"
                    />
                    <button
                      onClick={() => setShowPreview(false)}
                      className="absolute -top-2 -right-2 h-6 w-6 bg-content-3 rounded-full flex items-center justify-center text-surface hover:bg-content-2"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* RESUMEN Y BOTÓN DE ENVÍO                                           */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {canSend && effectivePoints && (
          <div className="bg-surface shadow rounded-lg animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="p-4 sm:p-6">
              <div className="bg-surface-2 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-content mb-3">Resumen</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-content-3">Destinatario</p>
                    <p className="font-medium text-content">
                      {selectedClient?.name || manualName || 'Sin nombre'}
                    </p>
                    <p className="text-xs text-content-3">
                      {selectedClient
                        ? `${selectedClient.countryCode} ${selectedClient.phone}`
                        : `${manualCountryCode} ${manualPhone}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-content-3">De parte de</p>
                    <p className="font-medium text-content">{senderName}</p>
                  </div>
                  {personalMessage.trim() && (
                    <div className="col-span-2">
                      <p className="text-content-3">Mensaje personal</p>
                      <p className="font-medium text-content whitespace-pre-line break-words">💌 «{personalMessage.trim()}»</p>
                    </div>
                  )}
                  <div>
                    <p className="text-content-3">{loyaltyMode === 'cashback' ? 'Saldo a regalar' : 'Puntos a regalar'}</p>
                    <p className="font-bold text-teal-700 dark:text-teal-300 text-lg">
                      {formatPoints(effectivePoints, loyaltyMode)}
                    </p>
                  </div>
                  <div>
                    <p className="text-content-3">Diseño</p>
                    <p className="font-medium text-content">
                      {availableTemplates.find((t) => t.id === selectedTemplateId)?.label || 'General'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-content-3">Estado</p>
                    <p className={`font-medium ${selectedClient ? 'text-green-600 dark:text-green-400' : 'text-amber-600'}`}>
                      {selectedClient ? (moneyMode ? 'Saldo de regalo inmediato' : 'Puntos inmediatos') : 'Pendiente de registro'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={handleReset}
                  disabled={sending}
                  className="text-sm text-content-3 hover:text-content-2"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || !canSend}
                  className={`inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-lg text-white transition-colors ${
                    sending
                      ? 'bg-line text-content-3 cursor-not-allowed'
                      : 'bg-teal-600 hover:bg-teal-700 shadow-sm'
                  }`}
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {sending ? 'Enviando...' : 'Enviar Gift Card'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* HISTORIAL DE GIFT CARDS                                            */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-surface shadow rounded-lg animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 sm:px-6 py-4 border-b border-line">
            <h3 className="text-sm font-semibold text-content flex items-center">
              <Clock className="h-4 w-4 text-teal-500 mr-2" />
              Historial de Gift Cards
            </h3>
          </div>

          {historyLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-teal-500" />
            </div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="h-10 w-10 text-content-3 mx-auto mb-2" />
              <p className="text-sm text-content-3">No has enviado gift cards aún</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-line">
                <thead className="bg-surface-2">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase">Destinatario</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase">De parte de</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase">{moneyMode ? 'Regalo' : 'Puntos'}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase">Diseño</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {history.map((item) => {
                    const tplInfo = GIFT_CARD_TEMPLATES.find((t) => t.id === item.templateId);
                    return (
                      <tr key={item.id} className="hover:bg-surface-2">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-content">
                            {item.recipientName || 'Sin nombre'}
                          </p>
                          <p className="text-xs text-content-3">{item.recipientPhone}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-content-2">{item.senderName || '—'}</span>
                          {item.message && (
                            <p className="text-xs text-content-3 mt-0.5 max-w-[16rem] truncate" title={item.message}>💌 {item.message}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-teal-700 dark:text-teal-300">
                            {/* Cada tarjeta en SU unidad: las viejas en puntos siguen diciendo puntos. */}
                            {item.unit === 'money' ? formatPoints(item.points, 'cashback') : formatPoints(item.points, loyaltyModeReal)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">{tplInfo?.icon} {tplInfo?.label || 'General'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              item.status === 'redeemed'
                                ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300'
                                : item.status === 'cancelled'
                                ? 'bg-surface-2 text-content-3'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {item.status === 'redeemed' ? 'Canjeada' : item.status === 'cancelled' ? 'Cancelada' : 'Pendiente'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-content-3">
                          {item.createdAt.toLocaleDateString('es-MX', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GiftCardPage;